import { normalizePhone } from './firebase';

export const splitIdentityName = (fullName: any) => {
  const safe = String(fullName || '').trim();
  if (!safe) {
    return { firstName: '', lastName: '' };
  }
  const parts = safe.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  };
};

export const formatOrganizerFirstName = (fullName: any, maxLength = 20): string => {
  const safe = String(fullName || '').trim();
  if (!safe) return '';

  if (/^(the\s+)?organizer$/i.test(safe)) {
    return 'Organizer';
  }

  // Strip parenthetical notes like "(Demo)" or "(Organizer)"
  const cleanName = safe.replace(/\s*\([^)]*\)/g, '').trim() || safe;

  // If it's a phone number:
  const digits = cleanName.replace(/\D/g, '');
  if ((digits.length >= 10 && cleanName.startsWith('+')) || /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/.test(cleanName)) {
    return 'Organizer';
  }

  const parts = cleanName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';

  // Clean leading/trailing non-word chars
  let first = parts[0].replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '').trim();
  if (!first) first = parts[0];

  // Gracefully truncate excessively long first names with an ellipsis
  if (first.length > maxLength) {
    return `${first.slice(0, maxLength - 1).trim()}…`;
  }
  return first;
};

export const formatFirstAndLastInitial = (fullName: any): string => {
  const safe = String(fullName || '').trim();
  if (!safe) return '';
  const parts = safe.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] || '';
  const firstName = parts[0];
  const lastPart = parts[parts.length - 1];
  const initial = lastPart.replace(/[^a-zA-Z0-9]/g, '')[0];
  if (!initial) return firstName;
  return `${firstName} ${initial.toUpperCase()}`;
};

const sanitizeCandidateName = (value: any, phone?: string) => {
  const normalizedPhone = normalizePhone(String(phone || ''));
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (trimmed.toLowerCase() === 'you') return '';
  if (trimmed.toLowerCase() === 'participant') return '';
  if (normalizedPhone && normalizePhone(trimmed) === normalizedPhone) return '';
  return trimmed;
};

export const sanitizeParticipantDisplayName = (value: any, phone?: string) =>
  sanitizeCandidateName(value, phone);

export const resolveCanonicalIdentityName = (record: any, phone?: string | null) => {
  const normalizedPhone = normalizePhone(String(phone || ''));
  if (!record) return '';

  const firstName = sanitizeCandidateName(record?.firstName, normalizedPhone);
  const lastName = sanitizeCandidateName(record?.lastName, normalizedPhone);
  const splitName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (splitName) return splitName;

  return sanitizeCandidateName(
    record?.name || record?.fullName || record?.displayName,
    normalizedPhone,
  );
};

export const buildCanonicalIdentityFields = (fullName: any) => {
  const { firstName, lastName } = splitIdentityName(fullName);
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return { firstName, lastName, name };
};

export const formatPhoneForDisplay = (phone?: string | null) => {
  const normalized = normalizePhone(String(phone || ''));
  const digits = normalized.replace(/[^\d]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return normalized || 'Participant';
};

export const resolveParticipantDisplayName = ({
  phone,
  viewerPhone,
  localName,
  canonicalName,
  persistedName,
  hasCleroAccount,
  fallbackLabel = 'Participant',
}: {
  phone?: string | null;
  viewerPhone?: string | null;
  localName?: string | null;
  canonicalName?: string | null;
  persistedName?: string | null;
  hasCleroAccount?: boolean | null;
  fallbackLabel?: string;
}) => {
  const normalizedPhone = normalizePhone(String(phone || ''));
  const normalizedViewerPhone = normalizePhone(String(viewerPhone || ''));


  const local = sanitizeCandidateName(localName, normalizedPhone);
  const canonical = sanitizeCandidateName(canonicalName, normalizedPhone);
  const persisted = sanitizeCandidateName(persistedName, normalizedPhone);
  const fallback = sanitizeCandidateName(fallbackLabel, normalizedPhone);

  if (canonical) return canonical;
  if (local) return local;
  if (persisted) return persisted;
  if (fallback) return fallback;

  return formatPhoneForDisplay(normalizedPhone) || 'Participant';
};
